import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search, Menu, X, User, LogIn, Star, TrendingUp,
  LogOut, Heart, Bell, ChevronDown, Zap, Disc3, Shield, Users, Bookmark, Mic2, ExternalLink, Settings
} from "lucide-react";
import { useTranslation } from "react-i18next";
import SearchOverlay from "./SearchOverlay";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { usePublicAuth } from "../context/PublicAuthContext";
import { LabelManagerBadge } from "./LabelManagerBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

import { UserNotificationBell } from "./UserNotificationBell";

export default function Header() {
  const [navPages, setNavPages] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user } = useAuth();
  const { settings } = useSettings();
  const { publicUser, isPublicAuth, login, logoutPublic } = usePublicAuth();
  const { t } = useTranslation();

  useEffect(() => {
    import("../utils/api").then(({ default: api }) => {
      api.get("/pages/nav")
        .then((res) => setNavPages(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    });
  }, []);

  useEffect(() => {
    const handler = () => setSearchOpen(true);
    window.addEventListener("openSearch", handler);
    return () => window.removeEventListener("openSearch", handler);
  }, []);

  const initials = (publicUser?.username || publicUser?.email || "?").slice(0, 2).toUpperCase();
  const isSupporter = publicUser?.is_supporter;
  const isArtist = Boolean(publicUser?.artist_id);
  const avatarUrl = publicUser?.avatar_url && !publicUser.avatar_url.includes("gravatar.com") ? publicUser.avatar_url : null;

  return (
    <>
      <header className="sticky top-0 z-40 bg-zinc-950/60 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo – erzwingt Reload + Scroll nach oben */}
            <a href="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={settings?.site_name || "HUWND"} className="h-8 w-auto object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-rose-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(225,29,72,0.4)]">
                    <span className="text-white font-black text-sm font-heading">H</span>
                  </div>
                  <span className="font-black text-white text-lg tracking-tight font-heading hidden sm:block">HUWND</span>
                </>
              )}
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7">
              {/* Home – erzwingt Reload */}
              <a href="/" data-testid="nav-home"
                className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 relative group py-1">
                {t("nav.home")}
                <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
              </a>
              {[
                { to: "/search",  label: t("nav.music"),   testId: "nav-archiv" },
                { to: "/artists", label: t("nav.artists"), testId: "nav-artists" },
                { to: "/labels",  label: t("nav.labels"),  testId: "nav-labels" },
              ].map(({ to, label, testId }) => (
                <Link key={to} to={to} data-testid={testId}
                  className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 relative group py-1">
                  {label}
                  <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
                </Link>
              ))}
              <Link to="/charts" data-testid="nav-charts"
                className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 py-1 relative group">
                <TrendingUp size={13} />
                {t("nav.charts")}
                <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
              </Link>
              {publicUser && (
                <Link to="/community" data-testid="nav-community"
                  className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 py-1 relative group">
                  <Users size={13} />
                  {t("nav.community")}
                  <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
                </Link>
              )}
              {(Array.isArray(navPages) ? navPages : []).map((page) => (
                <Link key={page.slug} to={`/page/${page.slug}`}
                  className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200">
                  {page.nav_label}
                </Link>
              ))}
              {(settings?.nav_links || []).map((link, i) => (
                link.is_external ? (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1 py-1 relative group">
                    {link.label}
                    <ExternalLink size={11} className="opacity-50" />
                    <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
                  </a>
                ) : (
                  <Link key={i} to={link.url}
                    className="text-zinc-500 hover:text-white text-sm font-medium transition-colors duration-200 py-1 relative group">
                    {link.label}
                    <span className="absolute inset-x-0 -bottom-px h-px bg-white/0 group-hover:bg-white/20 transition-colors duration-200" />
                  </Link>
                )
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="header-search-btn"
                className="p-2 text-zinc-500 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200"
                aria-label={t("header.open_search")}
              >
                <Search size={17} />
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User Notification Bell */}
              <UserNotificationBell isAuth={isPublicAuth} />

              {/* ── Authenticated User Dropdown ── */}
              {isPublicAuth ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-trigger"
                      className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl hover:bg-white/5 transition-all duration-200 group focus:outline-none"
                    >
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-7 h-7 rounded-full overflow-hidden ring-2 transition-all ${isArtist ? "ring-blue-500/40 shadow-[0_0_10px_rgba(59,130,246,0.2)]" : isSupporter ? "ring-rose-500/50 shadow-[0_0_10px_rgba(225,29,72,0.25)]" : "ring-white/10"}`}>
                          {avatarUrl ? (
                            <img src={avatarUrl} alt={publicUser.username} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-white text-[10px] font-bold">
                              {initials}
                            </div>
                          )}
                        </div>
                        {/* Supporter star indicator */}
                        {isSupporter && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-rose-600 rounded-full border-2 border-zinc-950 flex items-center justify-center">
                            <Star size={5} className="text-white fill-white" />
                          </span>
                        )}
                        {/* Artist disc indicator (wenn nur Artist, nicht Supporter) */}
                        {isArtist && !isSupporter && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-600 rounded-full border-2 border-zinc-950 flex items-center justify-center">
                            <Disc3 size={5} className="text-white" />
                          </span>
                        )}
                        {/* Beide: Supporter + Artist */}
                        {isArtist && isSupporter && (
                          <span className="absolute -bottom-0.5 -left-0.5 w-3 h-3 bg-blue-600 rounded-full border-2 border-zinc-950 flex items-center justify-center">
                            <Disc3 size={5} className="text-white" />
                          </span>
                        )}
                      </div>
                      <span className="text-zinc-300 text-sm font-medium group-hover:text-white transition-colors hidden sm:block max-w-[90px] truncate">
                        {publicUser?.username || "Profil"}
                      </span>
                      <ChevronDown size={12} className="text-zinc-600 group-hover:text-zinc-400 transition-colors hidden sm:block" />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="w-56 rounded-2xl bg-zinc-950/90 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.04)] overflow-hidden p-1.5 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                  >
                    {/* User info */}
                    <div className="px-3 pt-2.5 pb-3">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-semibold truncate flex-1">{publicUser?.username}</p>
                        {publicUser?.is_label_manager && <LabelManagerBadge size={14} />}
                      </div>
                      <p className="text-zinc-600 text-xs truncate mt-0.5">{publicUser?.email}</p>
                      {(isSupporter || isArtist || publicUser?.is_label_manager) && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {isSupporter && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 uppercase tracking-wider">
                              <Star size={7} className="fill-rose-400" /> Supporter
                            </span>
                          )}
                          {isArtist && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 uppercase tracking-wider">
                              <Disc3 size={7} /> Artist
                            </span>
                          )}
                          {publicUser?.is_label_manager && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                              style={{ background: "rgba(16,185,129,0.1)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)" }}>
                              <Disc3 size={7} /> Label
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="h-px bg-white/[0.07] mx-1 mb-1" />

                    {/* Öffentliches User-Profil */}
                    <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                      <Link
                        to={publicUser?.username ? `/user/${publicUser.username}` : "/profil"}
                        data-testid="menu-my-profile"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                        <User size={14} className="text-zinc-600" /> {t("header.my_profile")}
                      </Link>
                    </DropdownMenuItem>

                    {/* Profil bearbeiten */}
                    <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                      <Link to="/profil" data-testid="menu-edit-profile"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                        <Settings size={14} className="text-zinc-600" /> Profil bearbeiten
                      </Link>
                    </DropdownMenuItem>

                    {/* Artist Studio – für alle eingeloggten User */}
                    {publicUser?.artist_id || publicUser?.artist_ids?.length > 0 ? (
                      <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                        <Link to="/artist-studio" data-testid="menu-artist-studio"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                          <Mic2 size={14} className="text-rose-500/70" /> {t("header.artist_studio")}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                        <Link to="/artist-studio" data-testid="menu-artist-studio-apply"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-500 hover:bg-white/[0.07] hover:text-zinc-300 transition-colors cursor-pointer">
                          <Mic2 size={14} className="text-zinc-700" /> {t("header.become_artist")}
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {/* Label Studio – für alle eingeloggten User */}
                    <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                      <Link to="/label-studio" data-testid="menu-label-studio"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                        <Disc3 size={14} className="text-emerald-500/70" />
                        Label Studio
                      </Link>
                    </DropdownMenuItem>

                    {isSupporter ? (
                      <>
                        <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                          <Link to="/profil/favoriten" data-testid="menu-favorites"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                            <Bookmark size={14} className="text-amber-500/70" /> {t("header.saved")}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                          <Link to="/profil/follows" data-testid="menu-follows"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                            <Bell size={14} className="text-zinc-600" /> {t("header.following")}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                        <Link to="/supporter" data-testid="menu-become-supporter"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                          <Zap size={14} className="text-rose-500" />
                          {t("header.become_supporter")}
                          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 uppercase tracking-wider">
                            Pro
                          </span>
                        </Link>
                      </DropdownMenuItem>
                    )}

                    {/* Trennstrich + Admin-Bereich nur für Admin/Developer */}
                    {user && (publicUser?.role === "admin" || publicUser?.role === "developer") && (
                      <>
                        <div className="h-px bg-white/[0.07] mx-1 my-1" />
                        <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                          <Link to="/admin/dashboard" data-testid="menu-admin-dashboard"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-white/[0.07] hover:text-white transition-colors cursor-pointer">
                            <Shield size={14} className="text-zinc-600" /> {t("header.admin_area")}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}

                    <div className="h-px bg-white/[0.07] mx-1 my-1" />

                    <DropdownMenuItem
                      onClick={logoutPublic}
                      data-testid="logout-button"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-500 hover:bg-red-500/[0.08] hover:text-red-400 transition-colors cursor-pointer focus:bg-red-500/[0.08] focus:text-red-400"
                    >
                      <LogOut size={14} /> {t("header.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  onClick={() => login()}
                  data-testid="header-login-btn"
                  className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-xl bg-rose-600 hover:bg-rose-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_4px_14px_rgba(225,29,72,0.3)] hover:shadow-[0_6px_20px_rgba(225,29,72,0.4)] hover:-translate-y-0.5 transition-all duration-200"
                >
                  <LogIn size={14} /> {t("header.login")}
                </button>
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 text-zinc-500 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                aria-label={t("header.menu")}
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile Menu ── */}
        {menuOpen && (
          <div className="md:hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/[0.06] py-3 px-4">
            <nav className="flex flex-col gap-0.5">
              {/* Home – erzwingt Reload */}
              <a href="/" onClick={() => setMenuOpen(false)}
                className="text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">{t("nav.home")}</a>
              {[
                { to: "/search",  label: t("nav.music") },
                { to: "/artists", label: t("nav.artists") },
                { to: "/labels",  label: t("nav.labels") },
              ].map(({ to, label }) => (
                <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                  className="text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">{label}</Link>
              ))}
              <Link to="/charts" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">
                <TrendingUp size={14} /> {t("nav.charts")}
              </Link>
              {publicUser && (
                <Link to="/community" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-community"
                  className="flex items-center gap-2 text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">
                  <Users size={14} /> {t("nav.community")}
                </Link>
              )}
              {(Array.isArray(navPages) ? navPages : []).map((page) => (
                <Link key={page.slug} to={`/page/${page.slug}`} onClick={() => setMenuOpen(false)}
                  className="text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">
                  {page.nav_label}
                </Link>
              ))}
              {(settings?.nav_links || []).map((link, i) => (
                link.is_external ? (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">
                    {link.label} <ExternalLink size={12} className="opacity-40" />
                  </a>
                ) : (
                  <Link key={i} to={link.url} onClick={() => setMenuOpen(false)}
                    className="text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors">
                    {link.label}
                  </Link>
                )
              ))}
              <button onClick={() => { setMenuOpen(false); setSearchOpen(true); }}
                className="flex items-center gap-2 text-zinc-400 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors text-left">
                <Search size={14} /> {t("nav.search")}
              </button>

              {/* Login – nur für nicht eingeloggte User */}
              {!isPublicAuth && (
                <button onClick={() => { setMenuOpen(false); login(); }}
                  className="flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-white/5 px-3 py-2.5 rounded-xl text-sm transition-colors text-left">
                  <LogIn size={14} /> {t("header.login_register")}
                </button>
              )}

              {/* Admin – bleibt im Hamburger (Ausnahme) */}
              {user && (publicUser?.role === "admin" || publicUser?.role === "developer") && (
                <>
                  <div className="h-px bg-white/[0.06] my-1.5 mx-1" />
                  <Link to="/admin/dashboard" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-rose-500 hover:text-rose-400 hover:bg-rose-950/20 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                    <Shield size={14} /> {t("header.admin_area")}
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </>
  );
}
